import type { FieldValues, UseFormReturn } from "react-hook-form";
import { DeleteTravelerDialog } from "@apac/checkout";
import { useModuleBEM, useStyles } from "@b2bportal/core-themes";
import { useI18nContext } from "@hopper-b2b/i18n";
import {
  useTravellerForm,
  type UseTravellerFormParams,
  AdditionalInfo,
  type AdditionalInfoFieldOverrides,
  DriverLicense,
  type DriverLicenseFieldOverrides,
  FrequentFlyerDetails,
  type FrequentFlyerFieldOverrides,
  PassportDetails,
  type PassportDetailsFieldOverrides,
  TravelerDetails,
  type TravelerDetailsFieldOverrides,
  TravelerEditMode,
  ApacTravelersComponent,
  APAC_TRAVELERS_NAME,
} from "@tenant/apac-travelers";
import defaultStyles from "./styles.module.scss";

// TODO: This is a bit of a hack for now to account for multiple form contexts
type FormContext = UseFormReturn<FieldValues, unknown, undefined>;

export type TravellerFormSections = {
  travellerDetails?: TravelerDetailsFieldOverrides;
  additionalInfo?: AdditionalInfoFieldOverrides;
  passportDetails?: PassportDetailsFieldOverrides;
  frequentFlyer?: FrequentFlyerFieldOverrides;
  driverLicense?: DriverLicenseFieldOverrides;
};

export type NameFieldsType = "firstName" | "middleName" | "lastName";

interface TravellerFormProps extends UseTravellerFormParams {
  onClose: () => void;
  onDelete: (id: string) => void;
  dateFormat?: string;
  showPassportDetails?: boolean;
  fieldOverrides?: TravellerFormSections;
  subtitle?: string;
  showGender?: boolean;
  travelerEditMode?: TravelerEditMode;
  missingDriverInfoError?: boolean;
  missingPassportInfoError?: boolean;
  missingTravelerInfoError?: boolean;
  nameFieldsOrder?: NameFieldsType[];
  closeButtonText?: string;
  hideFlightsInfo?: boolean;
  hideDriverInfo?: boolean;
}

export const TravellerForm: React.FC<TravellerFormProps> = (props) => {
  const { t } = useI18nContext();
  const styles = useStyles(
    APAC_TRAVELERS_NAME,
    ApacTravelersComponent.TravellerForm,
    defaultStyles
  );
  const [block, cn] = useModuleBEM(
    styles,
    ApacTravelersComponent.TravellerForm
  );

  const {
    onClose,
    onDelete,
    traveler,
    fieldOverrides = {} as TravellerFormSections,
    showPassportDetails: showPassportDetailsProp,
    subtitle,
    showGender: showGenderProp = true,
    showNationality: showNationalityProp = false,
    requireGender: requireGenderProp,
    requireNationality: requireNationalityProp,
    requirePassportDetails: requirePassportDetailsProp,
    travelerEditMode,
    missingDriverInfoError = false,
    missingPassportInfoError = false,
    missingTravelerInfoError = false,
    nameFieldsOrder,
    closeButtonText,
    hideDriverInfo = false,
    hideFlightsInfo = false,
  } = props;

  // Apply the same "require implies show" defaults as useTravellerForm so the
  // rendered sections match the registered validation fields.
  const requireGender = requireGenderProp ?? showGenderProp;
  const requireNationality = requireNationalityProp ?? showNationalityProp;
  const requirePassportDetails =
    requirePassportDetailsProp ?? !!showPassportDetailsProp;

  const showGender = showGenderProp || requireGender;
  const showNationality = showNationalityProp || requireNationality;
  const showPassportDetails =
    !!showPassportDetailsProp || requirePassportDetails;

  const {
    travellerDetails = {},
    additionalInfo = {},
    passportDetails = {},
    frequentFlyer = {},
    driverLicense = {},
  } = fieldOverrides;

  const {
    context: { formContext, existingPassenger, frequentFlyerList },
    handlers: {
      onFrequentFlyerSubmit,
      onRemoveFrequentFlyer,
      onSubmitTraveller,
    },
  } = useTravellerForm(props);

  const showFlightsInfo =
    !hideFlightsInfo &&
    (travelerEditMode === TravelerEditMode.FLIGHTS_ONLY ||
      travelerEditMode === TravelerEditMode.ALL);

  const showDriverInfo =
    !hideDriverInfo &&
    (travelerEditMode === TravelerEditMode.CARS_ONLY ||
      travelerEditMode === TravelerEditMode.ALL);

  return (
    <form className={block} onSubmit={onSubmitTraveller}>
      <TravelerDetails
        fieldOverrides={travellerDetails}
        formContext={formContext as unknown as FormContext}
        subtitle={subtitle}
        showGender={showGender}
        showNationality={showNationality}
        missingInfoError={missingTravelerInfoError}
        nameFieldsOrder={nameFieldsOrder}
      />

      {showFlightsInfo && (
        <>
          {showPassportDetails && (
            <PassportDetails
              fieldOverrides={passportDetails}
              formContext={formContext as unknown as FormContext}
              missingInfoError={missingPassportInfoError}
            />
          )}

          <FrequentFlyerDetails
            fieldOverrides={frequentFlyer}
            formContext={formContext as unknown as FormContext}
            frequentFlyerList={frequentFlyerList}
            onFrequentFlyerSubmit={onFrequentFlyerSubmit}
            onRemoveFrequentFlyer={onRemoveFrequentFlyer}
          />

          <AdditionalInfo
            fieldOverrides={additionalInfo}
            formContext={formContext as unknown as FormContext}
          />
        </>
      )}

      {showDriverInfo && (
        <DriverLicense
          fieldOverrides={driverLicense}
          formContext={formContext as unknown as FormContext}
          missingInfoError={missingDriverInfoError}
        />
      )}

      <div className={cn("button-group")}>
        <button className={cn("submit-button")} type="submit">
          {t("saveTraveler")}
        </button>

        {existingPassenger && (
          <DeleteTravelerDialog
            text={{}}
            onConfirmDelete={() => {
              traveler && onDelete(traveler.id);
              onClose();
            }}
            trigger={
              <button className={cn("remove-button")} type="button">
                {t("delete")}
              </button>
            }
          />
        )}

        <button className={cn("close-button")} type="button" onClick={onClose}>
          {closeButtonText ?? t("close.button")}
        </button>
      </div>
    </form>
  );
};
